<template>
  <template v-if="localOptions.length === 1">
    <div v-for="option of options" :key="option.id">
      <SingleImageSelection :option="option" />
    </div>
  </template>
  <template v-else-if="localOptions.length > 1">
    <SingleImageSelection v-if="localModel" :option="localModel" />
    <div v-if="isSearchVisible" class="relative mt-7">
      <Icon name="search" class="absolute left-5 top-3 size-6" />
      <input
        v-model="searchQuery"
        type="text"
        class="!box-border w-full rounded-full border-border-medium py-3.5 pl-12 pr-4 text-text-medium transition-colors hover:border-border-medium-hover focus:outline-none"
        :placeholder="t['w.global.product_option.search']()"
      />
    </div>
    <div class="-mx-2 flex w-full flex-wrap justify-start py-4 md:-mx-3.5">
      <button
        v-for="option of filteredOptions"
        :key="option.id"
        class="group flex w-40 flex-col px-2 py-2.5 focus:outline-none md:w-auto md:basis-1/2 md:px-3.5 md:py-3"
        @click="() => onInput(option)"
      >
        <div class="relative w-full">
          <img
            v-if="option.smallImageUri || option.imageUri"
            :src="option.smallImageUri || option.imageUri"
            :alt="option.name"
            class="aspect-square border-2 border-border-light object-contain group-hover:border-2 group-hover:border-border-hover group-focus:border-border-active group-active:border-border-active"
          />
          <span
            v-if="option.isOptionAvailable === 0 || (option.isOptionAvailable === null && optionGroupCount === 1)"
            class="absolute inset-x-0 bottom-0 mx-0.5 mb-0.5 bg-text-attention/80 px-1.5 py-1 text-sm font-semibold text-text-inverted"
          >
            {{ t['w.global.product_option.not_available']() }}
          </span>
          <span
            v-if="option.isOptionAvailable === null && optionGroupCount > 1"
            class="absolute inset-x-0 bottom-0 mx-0.5 mb-0.5 bg-text-attention/80 px-1.5 py-1 text-sm font-semibold text-text-inverted"
          >
            {{ t['w.global.product_option.not_available_variation']() }}
          </span>
        </div>
        <p v-if="option.name" class="mt-2.5 px-0.5 text-left text-base font-semibold group-hover:text-text-strong group-focus:text-text-strong group-active:text-text-strong">
          {{ option.name }}
        </p>
      </button>
    </div>
  </template>
</template>

<script setup lang="ts">
import SingleImageSelection from './SingleImageSelection.vue'
import * as t from '~/messages'

const props = defineProps({
  options: { type: Array, default: null },
  model: { type: Object, default: null },
  optionGroupId: { type: Number, default: null },
  optionGroupTitle: { type: String, default: null },
  optionGroupDescription: { type: String, default: null },
  isOptionAvailable: { type: Function, default: null },
  selectedOptionIds: { type: Set, default: null },
  optionGroupCount: { type: Number, default: null },
})

const localModel = ref(props.model)
const localOptions = ref(props.options)
const searchQuery = ref('')
const isSearchVisible = localOptions.value.length >= 6

const filteredOptions = computed(() => {
  if (!searchQuery.value && !isSearchVisible) {
    return localOptions.value
  }
  const query = searchQuery.value.toLowerCase()

  return localOptions.value.filter((option) => option.name.toLowerCase().includes(query))
})

const emit = defineEmits<{
  'selected-option': [option: unknown]
  'local-model': [localModel: unknown]
}>()

const onInput = (option) => {
  emit('selected-option', option)
  emit('local-model', localModel.value)
}

watch(
  () => props.selectedOptionIds,
  () => {
    props.options.forEach((o) => {
      o.isOptionAvailable = props.isOptionAvailable(o.id, props.optionGroupId)
    })
    localOptions.value = {} // Vue DOM does not update on array push/pop, reset on each change to update DOM
    localOptions.value = props.options
  },
  {
    immediate: true,
  }
)
</script>
